import BigNumber from 'bignumber.js';

import { isNotNullable, isNullable } from './nullable';

BigNumber.config({
  EXPONENTIAL_AT: 1e9,
  FORMAT: {
    decimalSeparator: '.',
    groupSeparator: ',',
    groupSize: 3,
    secondaryGroupSize: 0,
    fractionGroupSeparator: ' ',
    fractionGroupSize: 0,
  },
  ROUNDING_MODE: BigNumber.ROUND_DOWN, // 기본 toFixed 내림 처리
});

export { BigNumber };

const Num = (n: string | number | BigNumber, base?: number): BigNumber => {
  if (typeof n === 'string') {
    return new BigNumber(n.replace(/,/g, ''), base);
  }

  return new BigNumber(n, base);
};

export default Num;

const dividedWithDot = (val: string, withComma: boolean) => {
  // 정상적인 정수가 아닌 경우("", "00100")케이스를 처리하기 위함.
  const stringToBigNumberAdapter = (value: string) => {
    if (value === '0' || value === '') return '0';

    const trimmed = value.replace(/^0+/, '');
    return trimmed || '0';
  };

  const NUMBER_REGEX = /[^\-0-9.]/g;
  const [integer, decimal] = val.replace(NUMBER_REGEX, '').split('.');

  if (integer === '-0') return [decimal ? '-0' : '0', decimal];
  const formattedInteger = stringToBigNumberAdapter(integer);
  if (withComma) return [Num(formattedInteger).toFormat(), decimal];
  return [Num(formattedInteger).toString(), decimal];
};

const substrPrecision = (
  value: string | null,
  maxPrecision?: number,
  withComma: boolean = false,
  maxLength?: number,
): string => {
  if (isNullable(value)) return '';
  const [integer, decimal] = dividedWithDot(value, withComma);

  if (!decimal || maxPrecision === 0) return integer;
  return `${integer}.${decimal.substring(0, maxPrecision ?? decimal.length)}`.substring(0, maxLength);
};

type RoundingMethod = 'no-round' | 'round-down' | 'round-up' | 'round-half-up' | 'round-half-down';
const bigNumberRoundingModeTable = {
  'round-down': BigNumber.ROUND_DOWN,
  'round-up': BigNumber.ROUND_UP,
  'round-half-up': BigNumber.ROUND_HALF_UP,
  'round-half-down': BigNumber.ROUND_HALF_DOWN,
} as const;

export interface NumberFormatterOptions {
  method?: RoundingMethod;
  withComma?: boolean;
  precision?: number;
  toFixed?: boolean;
}
const applyPrecisionMethod = (value: string, precision: number | undefined, method: RoundingMethod): string => {
  if (!isNotNullable(precision) || method === 'no-round') return value;
  return Num(value).toFixed(precision, bigNumberRoundingModeTable[method]);
};

const formatIntegerPart = (integer: string, withComma: boolean): string => {
  if (integer === '-0') return integer;
  return withComma ? Num(integer).toFormat() : integer;
};

const handleDecimalPart = (decimal: string, precision: number | undefined): string | null => {
  if (!decimal || precision === 0 || Num(decimal).isZero()) return null;
  const truncated = decimal.substring(0, precision ?? decimal.length);
  return Num(truncated).isZero() ? null : truncated;
};

const handleFixedDecimalPart = (decimal: string, precision: number): string => {
  return decimal.length > precision ? decimal.substring(0, precision) : decimal.padEnd(precision, '0');
};

const trimEndZeros = (value: string): string => {
  return value.replace(/0+$/, '');
};

/**
 * @param options.method - ('no-round' | 'round-down' | 'round-up' | 'round-half-up' | 'round-half-down')
 * @param options.withComma - 천 단위 구분자(,) (기본값: true)
 * @param options.precision - 소수점 이하 자릿수 표시
 * @param options.toFixed - 고정 소수점 표시 여부 (기본값: false)
 *   - true: precision 자릿수만큼 항상 표시 (뒤의 0 유지)
 *   - false: 불필요한 0 제거
 * @example
 * numberFormatter(1234.5678)  // "1,234.5678"
 * numberFormatter(1234.5678, { precision: 2 })  // "1,234.56"
 * numberFormatter(1234.5, { precision: 2, toFixed: true })  // "1,234.50"
 * numberFormatter(1234.56789, { precision: 2, method: 'round-half-down' })  // "1,234.57"
 * numberFormatter(1234.5678, { withComma: false })  // "1234.5678"
 */

export const numberFormatter = (
  value: BigNumber | string | number | undefined = 0,
  { method = 'no-round', withComma = true, precision, toFixed = false }: NumberFormatterOptions = {},
): string => {
  if (value === '') return '';
  const stringValue = value.toString();
  const processedValue = applyPrecisionMethod(stringValue, precision, method);

  const [integer, decimal = ''] = processedValue.split('.');
  const formattedInteger = formatIntegerPart(integer, withComma);

  const formattedDecimal = toFixed
    ? handleFixedDecimalPart(decimal, precision as number)
    : handleDecimalPart(decimal, precision);

  if (formattedDecimal === null) return formattedInteger;

  const finalDecimal = toFixed ? formattedDecimal : trimEndZeros(formattedDecimal);
  return finalDecimal !== '' ? `${formattedInteger}.${finalDecimal}` : formattedInteger;
};

interface InputFormatterOptions {
  precision?: number;
  withComma?: boolean;
  maxLength?: number;
}
/** 인풋에 올바른 숫자를 보여주도록 함 */
export const inputFormatter = (value?: string | number, options: InputFormatterOptions = {}) => {
  const { precision, withComma = false, maxLength } = options;
  if (!value) return '';
  const strNum = value.toString();
  const dotIndex = strNum.indexOf('.');
  const isLastDot = options.precision !== 0 && dotIndex === strNum.length - 1;
  const nextStr = substrPrecision(strNum, precision, withComma, maxLength);
  if (!isLastDot) return nextStr;
  return `${nextStr}.`;
};

const suffixes = new Map([
  ['one', 'st'],
  ['two', 'nd'],
  ['few', 'rd'],
  ['other', 'th'],
]);

export const getOrdinalNumber = (number: string | number | null) => {
  if (number === null) return { number: '', suffix: '' };
  const rule = new Intl.PluralRules('en-US', { type: 'ordinal' }).select(Number(number));
  return { number: number.toString(), suffix: suffixes.get(rule) };
};

export const truncateUnit = (number: number | undefined = 0) => {
  if (number < 10) return number?.toString();
  return `${Math.floor(number / 10) * 10}+`;
};

export const getNumberArr = (length: number): number[] => {
  return [...Array(length).keys()];
};

const NumberUnit = {
  K: 1_000,
  M: 1_000_000,
  B: 1_000_000_000,
  T: 1_000_000_000_000,
} as const;

export type GetNumberUnitOptions = {
  minUnit?: keyof typeof NumberUnit;
};

/**
 * 숫자를 단위(K, M, B, T)로 변환합니다.
 * @param strNumber - 변환할 숫자 문자열
 * @param options - 변환 옵션
 * @param options.minUnit - 최소 단위 (K, M, B, T). 지정된 단위보다 작은 값은 변환하지 않음
 * @returns {{ price: string; unit?: string }} 변환된 숫자와 단위
 * @example
 * getNumberUnit('1234')        // { price: '1.234', unit: 'K' }
 * getNumberUnit('1234567')     // { price: '1.234567', unit: 'M' }
 * getNumberUnit('1234', { minUnit: 'M' })  // { price: '1234', unit: '' }
 * getNumberUnit('1234567', { minUnit: 'M' })  // { price: '1.234567', unit: 'M' }
 */
export const getNumberUnit = (strNumber: string, options?: GetNumberUnitOptions): { price: string; unit?: string } => {
  const units = Object.entries(NumberUnit).reverse();
  for (const [unit, value] of units) {
    const divided = Num(strNumber).dividedBy(value);
    const absDivided = divided.abs();
    if (absDivided.gte(1) && absDivided.lt(1000)) {
      return { price: divided.toString(), unit };
    }
    if (unit === options?.minUnit) break;
  }
  return { price: strNumber, unit: '' };
};

/**
 * 숫자를 단위(K, M, B, T)와 함께 포맷팅합니다.
 * @param n - 포맷팅할 숫자 문자열
 * @param options - 포맷팅 옵션
 * @param options.precision - 소수점 자릿수 (기본값: 2)
 * @param options.withComma - 천 단위 구분자(,) 사용 여부 (기본값: true)
 * @param options.toFixed - 고정 소수점 표시 여부. true일 경우 precision 자릿수만큼 항상 표시 (기본값: false)
 * @param options.minUnit - 최소 단위 (K, M, B, T). 지정된 단위보다 작은 값은 변환하지 않음
 * @returns {string} 포맷팅된 문자열
 * @example
 * formatToShortNumber('1234')  // '1.23K'
 * formatToShortNumber('1234567')  // '1.23M'
 * formatToShortNumber('1234', { minUnit: 'M' })  // '1,234'
 * formatToShortNumber('1234', { withComma: false })  // '1.23K'
 */
export const formatToShortNumber = (
  n: string,
  options: NumberFormatterOptions & GetNumberUnitOptions = {
    withComma: true,
  },
) => {
  const shortInfo = getNumberUnit(n.toString(), {
    minUnit: options.minUnit,
  });
  return `${numberFormatter(shortInfo.price, {
    precision: options.precision ?? 2,
    toFixed: options.toFixed,
    withComma: options.withComma,
  })}${shortInfo.unit}`;
};

export const countDecimalPlaces = (number: number | string | undefined) => {
  if (!number) return 0;

  const decimalString = number.toString().split('.')[1];
  return decimalString ? decimalString.length : 0;
};
